import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './Carousel.css';
import { NFT } from '../../../services/models/NFT';
import { Link } from 'react-router-dom';

interface CarouselProps {
  items: NFT[];
}

const Carousel: React.FC<CarouselProps> = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const lastTouchMoveRef = useRef<number>(0);
  
  // State for responsive calculation
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Memoized responsive calculation for items per slide
  const { itemsPerSlide, itemWidth } = useMemo(() => {
    const width = windowWidth;
    let itemsPerSlide, itemWidth;
    
    if (width <= 480) {
      itemWidth = 248; // 240px + 8px margin for very small screens
      itemsPerSlide = Math.floor(width / itemWidth);
    } else if (width <= 768) {
      itemWidth = 292; // 280px + 12px margin for mobile
      itemsPerSlide = Math.floor(width / itemWidth);
    } else {
      // Desktop: slide longer distance (more items per slide)
      itemWidth = 346; // 330px + 16px margin for desktop
      const calculatedItems = Math.floor(width / itemWidth);
      itemsPerSlide = Math.max(calculatedItems, 3); // Minimum 3 items on desktop for longer slides
    }
    
    return { itemsPerSlide, itemWidth };
  }, [windowWidth]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + itemsPerSlide) % items.length);
  }, [items.length, itemsPerSlide]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - itemsPerSlide + items.length) % items.length);
  }, [itemsPerSlide, items.length]);

  // Optimized touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const touchX = e.touches[0].clientX;
    setStartX(touchX);
    setCurrentX(touchX);
    setDragOffset(0);
    lastTouchMoveRef.current = Date.now();
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    
    // Throttle touch move events to improve performance
    const now = Date.now();
    if (now - lastTouchMoveRef.current < 16) return; // ~60fps
    lastTouchMoveRef.current = now;
    
    e.preventDefault();
    const newCurrentX = e.touches[0].clientX;
    setCurrentX(newCurrentX);
    
    // Calculate drag offset for real-time sliding
    const offset = startX - newCurrentX;
    setDragOffset(offset);
  }, [isDragging, startX]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    const diff = startX - currentX;
    const threshold = 50; // Minimum distance for a swipe
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left - go to next slide
        nextSlide();
      } else {
        // Swiped right - go to previous slide
        prevSlide();
      }
    }
    
    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
    setDragOffset(0);
  }, [isDragging, startX, currentX, nextSlide, prevSlide]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isDragging) {
        nextSlide();
      }
    }, 7500); // Slide every 7.5 seconds

    return () => clearInterval(interval);
  }, [nextSlide, isDragging]);

  // Window resize handler to update responsive calculations
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setWindowWidth(window.innerWidth);
        // Reset carousel position when window resizes
        setCurrentIndex(0);
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Reset carousel when items change or component mounts
  useEffect(() => {
    setCurrentIndex(0);
  }, [items]);

  // Memoized transform calculation for better performance
  const transformValue = useMemo(() => {
    const baseTransform = currentIndex * itemWidth;
    const dragTransform = dragOffset * 0.7; // Increased drag responsiveness
    return baseTransform + dragTransform;
  }, [currentIndex, itemWidth, dragOffset]);

  return (
    <div className="carousel" ref={carouselRef}>
      <button className="carousel-arrow left" onClick={prevSlide}>
        &#10094;
      </button>
      <div 
        className="carousel-inner"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ 
          touchAction: 'pan-y',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none'
        }}
      >
        <div
          className="carousel-track"
          style={{
            transform: `translate3d(-${transformValue}px, 0, 0)`,
            transition: isDragging ? 'none' : 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            willChange: isDragging ? 'transform' : 'auto',
          }}
        >
          {items.map((item, index) => (
            <Link
            to={`/discover/ethereum/nft/${encodeURIComponent(item.name)}`}
              className="carousel-item"
              key={index}
            >
              <img 
                src={item.image_url} 
                alt={item.name} 
                className="carousel-image"
                loading="lazy"
                decoding="async"
                onLoad={(e) => {
                  // Ensure smooth loading
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.background = 'transparent';
                }}
                onError={(e) => {
                  // Fallback for failed images
                  e.currentTarget.style.display = 'none';
                }}
                style={{
                  opacity: 0,
                  transition: 'opacity 0.5s ease-in-out',
                  background: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                  backgroundSize: '20px 20px',
                  backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                }}
              />
              <div className="carousel-overlay">
                <h3 className="carousel-title">{item.name}</h3>
                <p className="carousel-description">{item.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <button className="carousel-arrow right" onClick={nextSlide}>
        &#10095;
      </button>
    </div>
  );
};

export default React.memo(Carousel);
